import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  LinearProgress,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Check,
  Close,
  Star,
  WorkspacePremium,
  Timer,
  Bolt,
  Diamond,
  BusinessCenter,
} from '@mui/icons-material';
import { useBilling } from '../hooks/useBilling';
import { fetchWithAuth } from '../lib/api';

const PLAN_CARDS = [
  {
    key: 'free',
    name: 'Free',
    price: '€0',
    period: 'για πάντα',
    employees: 'Έως 5 ενεργοί',
    icon: <Bolt />,
    features: [
      { text: 'Βάρδιες & Πληρωμές', ok: true },
      { text: 'Έσοδα & Έξοδα', ok: true },
      { text: 'Dashboard', ok: true },
      { text: 'Email invitations & ρόλοι', ok: true },
      { text: 'myDATA Sync', ok: false },
      { text: 'Export PDF/Excel', ok: false },
      { text: 'AI Assistant', ok: false },
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '€9,90',
    period: '/μήνα',
    employees: 'Έως 10 ενεργοί',
    icon: <Diamond />,
    popular: true,
    features: [
      { text: 'Όλα τα features', ok: true },
      { text: 'myDATA Sync', ok: true },
      { text: 'Export PDF/Excel', ok: true },
      { text: 'AI Assistant', ok: true },
    ],
  },
  {
    key: 'business',
    name: 'Business',
    price: '€19',
    period: '/μήνα',
    employees: 'Απεριόριστοι ενεργοί',
    icon: <BusinessCenter />,
    features: [
      { text: 'Όλα τα Pro features', ok: true },
      { text: 'Απεριόριστοι ενεργοί υπάλληλοι', ok: true },
    ],
  },
];

export default function BillingSettings() {
  const queryClient = useQueryClient();
  const {
    subscription,
    isLoading,
    refetchOrganizations,
    effectivePlan,
    isTrialing,
    trialDaysRemaining,
    activeEmployeeCount,
    maxActiveEmployees,
    needsEmployeeReduction,
    canStartTrial,
    demoMode,
  } = useBilling();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
    refetchOrganizations();
  };

  const trialMutation = useMutation({
    mutationFn: async (trialPlan) => {
      const res = await fetchWithAuth('/api/billing/start-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trialPlan }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to start trial');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setSnackbar({ open: true, message: 'Η δοκιμαστική περίοδος ξεκίνησε!', severity: 'success' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.message, severity: 'error' }),
  });

  const demoUpgradeMutation = useMutation({
    mutationFn: async (plan) => {
      const res = await fetchWithAuth('/api/billing/demo-upgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to upgrade');
      }
      return res.json();
    },
    onSuccess: (_, plan) => {
      invalidate();
      setSnackbar({ open: true, message: `Αναβαθμίστηκες σε ${plan}!`, severity: 'success' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.message, severity: 'error' }),
  });

  const demoDowngradeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetchWithAuth('/api/billing/demo-downgrade', { method: 'POST' });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to downgrade');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setSnackbar({ open: true, message: 'Υποβαθμίστηκες σε Free.', severity: 'info' });
    },
    onError: (err) => setSnackbar({ open: true, message: err.message, severity: 'error' }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#008060' }} />
      </Box>
    );
  }

  const isCurrent = (planKey) => {
    if (isTrialing) return effectivePlan === planKey;
    return effectivePlan === planKey && !isTrialing;
  };

  return (
    <Box>
      {demoMode && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2.5 }}>
          <strong>Demo mode:</strong> Χρησιμοποίησε τα κουμπιά για δοκιμή αναβάθμισης.
        </Alert>
      )}

      {needsEmployeeReduction && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2.5 }}>
          Έχετε {activeEmployeeCount} ενεργούς υπαλλήλους αλλά το Free επιτρέπει έως {maxActiveEmployees}.
          Μειώστε ενεργούς στο Προσωπικό ή αναβαθμίστε.
        </Alert>
      )}

      <Paper sx={{
        p: { xs: 2.5, md: 3 },
        mb: 3,
        borderRadius: 3,
        border: '1px solid rgba(0,128,96,0.15)',
        background: 'linear-gradient(135deg, rgba(0,128,96,0.08) 0%, #fff 100%)',
      }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: isTrialing ? 2 : 0 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            bgcolor: 'primary.main', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <WorkspacePremium />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
              Τρέχον πλάνο
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {isTrialing ? `${effectivePlan === 'business' ? 'Business' : 'Pro'} Trial` : effectivePlan === 'business' ? 'Business' : effectivePlan === 'pro' ? 'Pro' : 'Free'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeEmployeeCount} ενεργοί υπάλληλοι
              {maxActiveEmployees ? ` / όριο ${maxActiveEmployees}` : ' / απεριόριστοι'}
            </Typography>
          </Box>
          {isTrialing && (
            <Chip icon={<Timer />} label={`${trialDaysRemaining} ημέρες`} color="warning" sx={{ fontWeight: 700 }} />
          )}
        </Stack>
        {isTrialing && trialDaysRemaining !== null && (
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, ((14 - trialDaysRemaining) / 14) * 100))}
            sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(237,108,2,0.12)', '& .MuiLinearProgress-bar': { bgcolor: '#ed6c02' } }}
          />
        )}
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Πλάνα</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {PLAN_CARDS.map((plan) => (
          <Grid item xs={12} md={4} key={plan.key}>
            <Paper sx={{
              p: 2.5,
              height: '100%',
              borderRadius: 3,
              border: '2px solid',
              borderColor: isCurrent(plan.key) ? 'primary.main' : 'rgba(0,0,0,0.08)',
              boxShadow: isCurrent(plan.key) ? '0 8px 24px rgba(0,128,96,0.12)' : 'none',
              position: 'relative',
            }}>
              {plan.popular && !isCurrent(plan.key) && (
                <Chip label="Δημοφιλές" size="small" color="primary" sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 700 }} />
              )}
              {isCurrent(plan.key) && (
                <Chip label="Τρέχον" size="small" sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 700, bgcolor: 'rgba(0,128,96,0.12)', color: 'primary.main' }} />
              )}
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(0,128,96,0.1)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                {plan.icon}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{plan.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{plan.employees}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {plan.price}
                <Typography component="span" variant="body2" color="text.secondary">{plan.period}</Typography>
              </Typography>
              {plan.key !== 'free' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Ετήσια: -20% έκπτωση
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1}>
                {plan.features.map((f) => (
                  <Box key={f.text} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {f.ok ? <Check sx={{ fontSize: 16, color: 'primary.main' }} /> : <Close sx={{ fontSize: 16, color: 'text.disabled' }} />}
                    <Typography variant="body2" color={f.ok ? 'text.primary' : 'text.disabled'}>{f.text}</Typography>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ mt: 2 }}>
                {plan.key === 'free' && effectivePlan !== 'free' && demoMode && (
                  <Button fullWidth variant="outlined" color="error" onClick={() => demoDowngradeMutation.mutate()} disabled={demoDowngradeMutation.isPending} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Υποβάθμιση σε Free
                  </Button>
                )}
                {plan.key !== 'free' && !isCurrent(plan.key) && canStartTrial && effectivePlan === 'free' && (
                  <Button fullWidth variant="outlined" startIcon={<Timer />} onClick={() => trialMutation.mutate(plan.key)} disabled={trialMutation.isPending} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, mb: 1 }}>
                    Δοκιμή 14 ημερών
                  </Button>
                )}
                {plan.key !== 'free' && !isCurrent(plan.key) && demoMode && (
                  <Button fullWidth variant="contained" startIcon={<Star />} onClick={() => demoUpgradeMutation.mutate(plan.key)} disabled={demoUpgradeMutation.isPending} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    {demoUpgradeMutation.isPending ? 'Αναβάθμιση...' : `Αναβάθμιση σε ${plan.name}`}
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
